import {
  clearPendingSession,
  readPendingSession,
  storePendingSession,
} from "../pending-session";
import { paymentSessionIdFromQuery } from "../return-status";
import { PaymentProviderName } from "@/lib/config";
import type {
  PaymentConfirmation,
  PaymentOutcome,
  PaymentProvider,
  PaymentSession,
  ReturnContext,
} from "../types";

export class StripePaymentProvider implements PaymentProvider {
  readonly name = PaymentProviderName.Stripe;

  async startCheckout(session: PaymentSession): Promise<void> {
    if (!session.checkoutUrl) {
      throw new Error("Stripe checkout session is missing checkoutUrl.");
    }

    storePendingSession({
      paymentSessionId: session.paymentSessionId ?? session.sessionId,
      providerSessionId: session.sessionId,
      provider: this.name,
      orderReference: session.orderReference,
    });

    window.location.assign(session.checkoutUrl);
  }

  parseReturn(ctx: ReturnContext): PaymentConfirmation {
    const pending = readPendingSession();
    const providerSessionId =
      stripeSessionIdFromQuery(ctx.query) ?? pending?.providerSessionId;
    const outcome = stripeOutcomeFromQuery(ctx.query);
    clearPendingSession();
    return {
      sessionId:
        pending?.paymentSessionId ??
        paymentSessionIdFromQuery(ctx.query) ??
        providerSessionId ??
        "",
      paymentProviderSessionId: providerSessionId,
      provider: this.name,
      outcome,
      orderReference: pending?.orderReference,
      resultCode: stripeResultCodeFromOutcome(outcome),
      providerData: { ...ctx.query },
    };
  }
}

function stripeSessionIdFromQuery(query: ReturnContext["query"]): string | undefined {
  return firstQueryValue(
    query.session_id ??
      query.checkout_session_id ??
      query.paymentProviderSessionId ??
      query.sessionId,
  );
}

function stripeOutcomeFromQuery(query: ReturnContext["query"]): PaymentOutcome {
  const status = firstQueryValue(query.status ?? query.redirect_status)?.toLowerCase();
  if (status === "succeeded" || status === "complete" || status === "paid") return "success";
  if (status === "processing" || status === "requires_action") return "pending";
  if (status === "failed" || status === "canceled" || status === "cancelled") return "failed";
  return firstQueryValue(query.session_id ?? query.checkout_session_id) ? "pending" : "unknown";
}

function stripeResultCodeFromOutcome(outcome: PaymentOutcome): string | undefined {
  if (outcome === "success") return "succeeded";
  if (outcome === "pending") return "processing";
  if (outcome === "failed") return "failed";
  return undefined;
}

function firstQueryValue(value: ReturnContext["query"][string]): string | undefined {
  if (Array.isArray(value)) {
    return value.find((item): item is string => typeof item === "string" && item.length > 0);
  }
  return typeof value === "string" && value.length > 0 ? value : undefined;
}
